/**
 * Sektor- und Tragweitenlogik nach Regel 21 und 22 (33 CFR 83.21, 83.22).
 * Reine Funktionen ohne Three.js, damit sie im Unit-Test prüfbar sind.
 */
import type { LengthClass, LightType } from './types'

/** Peilung des Beobachters vom Fahrzeug aus: 0 Grad = recht voraus, im Uhrzeigersinn. */
export type Bearing = number

export interface Sector {
  /** Oeffnungswinkel in Grad. */
  arc: number
  /** Mitte des Sektors als Peilung. */
  centre: Bearing
}

/** Regel 21: Sichtbarkeitssektoren. 22,5 Grad achterlicher als querab = 112,5 Grad. */
export const SECTORS: Record<LightType, Sector> = {
  masthead: { arc: 225, centre: 0 },
  'sidelight-stbd': { arc: 112.5, centre: 56.25 },
  'sidelight-port': { arc: 112.5, centre: 303.75 },
  'sidelights-combined': { arc: 225, centre: 0 },
  sternlight: { arc: 135, centre: 180 },
  towing: { arc: 135, centre: 180 },
  allround: { arc: 360, centre: 0 },
  'special-flashing': { arc: 225, centre: 0 },
  handlamp: { arc: 360, centre: 0 },
}

export function normaliseBearing(deg: number): Bearing {
  const x = deg % 360
  return x < 0 ? x + 360 : x
}

/** Kleinster Winkelabstand zwischen zwei Peilungen, 0 bis 180. */
export function angularDistance(a: Bearing, b: Bearing): number {
  const d = Math.abs(normaliseBearing(a) - normaliseBearing(b))
  return d > 180 ? 360 - d : d
}

/**
 * Sichtbarkeit eines Lichts an der Peilung `bearing`, 0 (aus) bis 1 (voll).
 *
 * Innerhalb des vorgeschriebenen Sektors brennt das Licht voll: Regel 21 verlangt
 * einen ununterbrochenen Schein über den ganzen Bogen, also auch genau recht voraus
 * und genau an der Grenze 22,5 Grad achterlicher als querab. Weich wird der Rand
 * erst dahinter, über `softEdge` Grad, weil ein Laternenrand in der Natur nicht
 * schneidet. Bei softEdge=0 ist die Kante hart.
 */
export function sectorVisibility(type: LightType, bearing: Bearing, softEdge = 2): number {
  const sector = SECTORS[type]
  if (sector.arc >= 360) return 1
  const half = sector.arc / 2
  const d = angularDistance(bearing, sector.centre)
  if (d <= half) return 1
  if (softEdge <= 0) return 0
  if (d >= half + softEdge) return 0
  return (half + softEdge - d) / softEdge
}

export function isVisibleInSector(type: LightType, bearing: Bearing): boolean {
  return sectorVisibility(type, bearing, 0) > 0
}

/** Regel 22: Mindesttragweiten in Seemeilen. */
export const NOMINAL_RANGE_NM: Record<LengthClass, Partial<Record<LightType, number>>> = {
  ge50: {
    masthead: 6,
    'sidelight-port': 3,
    'sidelight-stbd': 3,
    'sidelights-combined': 3,
    sternlight: 3,
    towing: 3,
    allround: 3,
    'special-flashing': 2,
    handlamp: 2,
  },
  m20to50: {
    masthead: 5,
    'sidelight-port': 2,
    'sidelight-stbd': 2,
    'sidelights-combined': 2,
    sternlight: 2,
    towing: 2,
    allround: 2,
    'special-flashing': 2,
    handlamp: 2,
  },
  // Regel 22(b)(i): unter 20 m beträgt die Tragweite des Topplichts 3 sm.
  m12to20: {
    masthead: 3,
    'sidelight-port': 2,
    'sidelight-stbd': 2,
    'sidelights-combined': 2,
    sternlight: 2,
    towing: 2,
    allround: 2,
    'special-flashing': 2,
    handlamp: 2,
  },
  m7to12: {
    masthead: 2,
    'sidelight-port': 1,
    'sidelight-stbd': 1,
    'sidelights-combined': 1,
    sternlight: 2,
    towing: 2,
    allround: 2,
    'special-flashing': 2,
    handlamp: 2,
  },
  lt7: {
    masthead: 2,
    'sidelight-port': 1,
    'sidelight-stbd': 1,
    'sidelights-combined': 1,
    sternlight: 2,
    towing: 2,
    allround: 2,
    'special-flashing': 2,
    handlamp: 2,
  },
}

export function nominalRangeNm(type: LightType, lengthClass: LengthClass): number {
  return NOMINAL_RANGE_NM[lengthClass][type] ?? 2
}

/**
 * Scheinbare Helligkeit eines Lichts aus der Entfernung `distanceNm`, 0 bis 1.
 *
 * Die Tragweite aus Regel 22 ist die Entfernung, bei der ein Licht gerade noch
 * auszumachen ist. Bis dahin bleibt es sichtbar und wird zum Rand hin schwächer,
 * es fällt also nicht mit dem Quadrat der Entfernung wie die Beleuchtungsstärke:
 * gezeigt wird, was das Auge sieht, nicht was der Luxmeter misst.
 */
export function apparentIntensity(distanceNm: number, rangeNm: number): number {
  if (rangeNm <= 0) return 0
  if (distanceNm >= rangeNm) return 0
  if (distanceNm <= 0) return 1
  return (1 - distanceNm / rangeNm) ** 0.55
}

export const NM_IN_METERS = 1852
